<template>
    <header class="s-cpoc-header">
        <div class="a-container">
            <div class="s-cpoc-header__content">
                <div ref="mainHeaderLogo" class="s-cpoc-header__logo-container">
                    <dynamic-link :to="logoLink">
                        <a-tru-logo class="s-cpoc-header__logo" />
                    </dynamic-link>
                </div>
                <div v-if="menuIsAvailable" ref="mainHeaderMenu" class="s-cpoc-header__nav">
                    <ul id="s-cpoc-header-menu" class="s-cpoc-header__menu">
                        <li class="desktop-links">
                            <a-link
                                v-for="item in links"
                                :key="item.to"
                                v-bind="item"
                                class="link"
                                :to="item.to"
                                :text="item.text"
                            />
                        </li>
                        <li v-if="truSearchLink" class="s-cpoc-header__menu-item">
                            <dynamic-link :to="truSearchLink" class="tru-search-link">
                                <a-glyph name="search" />
                            </dynamic-link>
                        </li>
                        <li class="locale-select s-cpoc-header__menu-item">
                            <button
                                class="locale-select-button"
                                type="button"
                                @click.prevent.stop="onLocaleSelectClick"
                            >
                                <a-glyph class="glyph" name="language-select2" />
                            </button>
                            <el-select
                                ref="select"
                                :model-value="{}"
                                class="select"
                                name="country"
                                popper-class="dropdown locale-dropdown color-scheme-tru"
                                should-teleport
                                @change="onLocationChange"
                            >
                                <el-option
                                    v-for="item in locales"
                                    :key="item.code"
                                    :label="item.language"
                                    :value="item.code"
                                />
                            </el-select>
                        </li>
                    </ul>
                </div>
                <button
                    v-if="panelIsAvailable"
                    ref="menuToggler"
                    class="menu-toggler"
                    :class="{ 'menu-toggler_state_active': isActive}"
                    type="button"
                    tabindex="1"
                    @click.prevent.stop="toggleMenu"
                >
                    <a-glyph class="menu-toggler__icon" :name="getMenuTogglerIcon" />
                </button>
            </div>
            <div v-if="title || lead" class="head">
                <h1 v-if="title" class="title">
                    {{ title }}
                </h1>
                <h2 v-if="lead" class="lead">
                    {{ lead }}
                </h2>
            </div>
        </div>

        <div v-show="isActive" ref="menu" class="s-main-menu">
            <a-link
                v-for="item in links"
                :key="item.to"
                v-bind="item"
                class="link"
                :to="item.to"
                :text="item.text"
                size="lead"
            />
        </div>
    </header>
</template>

<script>
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';
import ElOption from '@core/components/select/option.vue';
import ElSelect from '@core/components/select/select.vue';
import ATruLogo from '@core/components/tru-logo/tru-logo.vue';
import breakpoint from '@core/mixins/breakpoint.js';
import { getLanguageCodeFromLocale } from '@utils/languages';

export default {
    name: 'SBlogCpcHeader',

    components: {
        DynamicLink,
        AGlyph,
        ALink,
        ATruLogo,
        ElSelect,
        ElOption,
    },

    mixins: [breakpoint],

    props: {
        /**
         * Theme
         */
        theme: {
            type: String,
            default: 'dark',
            required: false,
        },

        /**
         * Ability to change the link of the logo
         */
        logoLink: {
            type: String,
            default: '/tru',
            required: false,
        },

        /**
         * menu is available
         */
        menuIsAvailable: {
            type: Boolean,
            default: true,
        },

        /**
         * panel is available
         */
        panelIsAvailable: {
            type: Boolean,
            default: true,
        },

        /**
         * Locale selector is available
         */
        localeSelectorIsAvailable: {
            type: Boolean,
            default: true,
        },

        /**
         * List of links to update
         */
        listToUpdate: {
            type: Array,
            default: null,
        },

        /**
         * Link to search page
         */
        truSearchLink: {
            type: String,
            default: null,
        },

        /**
         * List of locales
         */
        locales: {
            type: Array,
            required: true,
        },

        title: {
            type: String,
            default: null,
        },

        lead: {
            type: String,
            default: null,
        },

        links: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            isActive: false,
            loginState: null,
            isLocaleSelectOpened: false,
        };
    },

    computed: {
        getMenuTogglerIcon() {
            //  TODO: Invalid prop: custom validator check failed for prop "name
            return this.isActive ? 'times' : 'burger-menu';
        },
    },

    watch: {
        isDesktop() {
            this.hideMenu();
        },
    },

    methods: {
        toggleMenu() {
            this.isActive = !this.isActive;
        },

        hideMenu() {
            this.isActive = false;
        },

        onLocaleSelectClick() {
            this.$refs.select.$refs.triggerRef.$el.click();
        },

        async onLocationChange(locale) {
            if (!locale) return;

            const language = getLanguageCodeFromLocale(locale);

            const pathSplit = this.$route.path.split('/').filter(Boolean);
            pathSplit.shift(); // Removes original language
            let url = `/${language}/${pathSplit.join('/')}`;
            url += url.endsWith('/') ? '' : '/';

            const opts = {
                timeout: 10000,
                validateStatus: (status) => status === StatusCodes.OK,
                method: 'HEAD',
                url,
            };

            try {
                await axios.request(opts);
                window.location.href = url;
            } catch {
                window.location.href = `/${language}/tru/`;
            }
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-cpoc-header {
    position: absolute;
    top: 24px;
    width: 100%;
    z-index: 400;

    &__logo {
        height: 48px;
        width: 132px;
        &-container {
            margin: 0;
            @media (--viewport-desktop) {
                margin-inline-end: 53px;
            }
            @media (--viewport-desktop-wide) {
                margin-inline-end: 148px;
            }
        }
    }

    &__content {
        z-index: 400;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__nav {
        z-index: 100;
    }

    &__menu {
        position: absolute;
        inset-inline-end: 64px;
        inset-inline-start: auto;
        top: 12px;
        border-inline-end: 1px solid var(--av-inversed-primary);
        height: 24px;
        padding-inline-end: 16px;
        display: flex;
        width: auto;
        margin-inline-end: auto;

        @media (--viewport-mobile-wide) {
            inset-inline-end: 84px;
        }

        @media (--viewport-desktop) {
            height: auto !important;
            top: 100%;
            position: static;
            inset-inline-start: 0;
            border: 0;
            padding-inline-end: 0;
        }

        .desktop-links {
            display: none;

            @media (--viewport-desktop) {
                display: block;
            }
        }

        .link {
            padding: 0 12px;

            :deep(.a-link__content) {
                @mixin paragraph-accent;

                color: var(--av-inversed-primary);
            }

            &:hover {
                :deep(.a-link__content) {
                    color: var(--av-link-inversed-hover);
                }
            }
        }
    }

    .menu-toggler {
        padding: 0;
        border: none;
        display: flex;
        z-index: 100;
        height: 32px;
        min-width: 40px;
        cursor: pointer;
        background: none;
        position: absolute;
        inset-inline-end: var(--container-paddings-mobile);
        align-items: center;
        justify-content: flex-end;
        margin-top: -6px;

        @media (--viewport-mobile-wide) {
            inset-inline-end: var(--container-paddings-mobile-wide);
        }

        &__icon {
            width: 32px;
            height: 32px;
            transition: none;
            fill: var(--av-inversed-primary);
        }

        &_state {
            &_active {
                .menu-toggler {
                    &__icon {
                        width: 24px;
                        height: 24px;
                        margin-inline-end: 4px;
                    }
                }
            }
        }

        @media (--viewport-desktop) {
            display: none;
        }
    }

    .s-main-menu {
        inset-inline-start: 0;
        inset-inline-end: 0;
        top: 48px;
        width: 100%;
        background: var(--av-inversed-primary);
        position: absolute;
        min-height: calc(100vh - 75px);
        z-index: 100;
        border-top: 2px solid var(--av-nav-secondary);
        box-shadow: var(--av-shadow-regular);

        @media (--viewport-tablet) {
            top: 56px;
        }

        .link {
            @mixin lead-accent;
            display: block;
            padding: 16px 40px 16px 32px;
        }
    }

    .tru-search-link {
        padding: 0 8px;
        display: flex;
        height: 100%;
        align-items: center;

        .a-glyph {
            fill: var(--av-inversed-primary);
        }
    }

    .select {
        opacity: 0;
        pointer-events: none;
        position: absolute;
        width: 320px;
        top: 32px;
        inset-inline-end: 0;

        @media (--viewport-tablet) {
            top: 0;
        }
    }

    .locale-select-button {
        display: flex;
        align-items: center;
        height: 100%;
        padding: 0 8px;
        margin-inline-start: 8px;
        cursor: pointer;

        .a-glyph {
            fill: var(--av-inversed-primary);
        }

        &:hover {
            .a-glyph {
                fill: var(--av-link-inversed);
            }
        }
    }
}

.head {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: center;
    margin-top: 32px;
    padding-bottom: 24px;
    color: var(--av-inversed-primary);
    text-align: center;
    @media (--viewport-tablet) {
        text-align: start;
        justify-content: space-between;
    }
    @media (--viewport-desktop) {
        flex-wrap: nowrap;
    }
    .title {
        font-weight: 700;
        margin-bottom: 16px;
        font-size: 32px;
        line-height: 56px;
        max-width: 700px;
        @media (--viewport-tablet) {
            font-size: 48px;
            width: 50%;
            margin-bottom: 0;
        }
        @media (--viewport-desktop) {
            font-size: 64px;
            width: auto;
            line-height: 72px;
        }
    }
    .lead {
        @mixin title;
        @media (--viewport-tablet) {
            width: 50%;
            max-width: 250px;
        }
        @media (--viewport-desktop) {
            @mixin display;
            width: auto;
            max-width: 340px;
        }
    }
}

[dir='rtl'] {
    .s-cpoc-header {
        .s-main-menu {
            &.s-main-menu_centered {
                inset-inline-start: auto;
                inset-inline-end: unset;
            }
        }
    }
    .s-cpoc-header__menu-link::before,
    .s-cpoc-header__menu-link::after {
        inset-inline-start: unset;
        inset-inline-end: 50%;
    }
}
</style>
<style lang="postcss">
.el-select-dropdown.dropdown.locale-dropdown {
    padding: 12px 8px;
    min-width: 0;
    width: 236px;
    border-radius: 4px;
    border: 1px solid var(--av-brand-primary);
    background: var(--av-inversed-primary);
    box-shadow: var(--av-shadow-regular);

    .el-scrollbar .el-select-dropdown__item {
        @mixin descriptor-accent;
        padding: 0 8px;
        height: 28px;
        border-radius: 4px;

        span {
            line-height: 28px !important;
        }

        &:hover {
            background: var(--av-fixed-lightest);
        }
    }

    .el-scrollbar__view {
        padding: 0;
    }

    .el-input__editor {
        font-size: 16px;
    }
}
</style>
